import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getAllSites } from 'state/reducers/user.reducers';
import { createNewCp, getAllCps, setError } from 'state/reducers/cp.reducers';
import { updateSiteCpCount } from 'state/reducers/site.reducers';

import { Box, Button, Stack } from '@mui/material';

//styled
import { StyledCard, StyledCardContent, StyledTypographyTitle } from '../../styled-components';

//constants
import { CPInputsFormLabels, CpInputsForm, ICp } from 'constants/cp.constants';
import { pagesEnum } from 'constants/ui.constants';
//components
import CustomInputForm from 'components/InputsForm/CustomInput';
//icons
import { CpIcon } from 'assets/icons/Cp.icon';
import { Site } from 'constants/sites.constants';
import SelectBox from 'components/SelectBox';
import CircularIndeterminate from 'components/Loading';
import ConfirmDialog from 'components/ConfirmDialog';
import { formResolver } from '../../../utils/global.util';

interface IFormInputs {
  dataForm: CpInputsForm;
  cp: ICp;
  setOpenProfileModal: Dispatch<SetStateAction<boolean>>;
}

const NewCp: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sites, loading: userLoading, error: sitesError } = useAppSelector((state) => state.user);
  const { cps, loading: cpsLoading, error: cpsError } = useAppSelector((state) => state.cp);
  const { userDetails } = useAppSelector((state) => state.auth);
  const [assignSite, setAssignSite] = useState('');
  const [idNumberDuplicate, setIdNumberDuplicate] = useState<string>('');

  useEffect(() => {
    dispatch(getAllSites());
    dispatch(getAllCps({}));
  }, []);

  const methods = useForm<IFormInputs>({
    defaultValues: {},
    mode: 'onTouched',
    resolver: (values) => formResolver(values, [{ key: 'idNumber', displayName: CPInputsFormLabels.ID_NUMBER }])
  });

  const {
    formState: { errors }
  } = methods;

  const hasError = useMemo(
    () => !!cpsError || !!sitesError || !!idNumberDuplicate,
    [cpsError, sitesError, idNumberDuplicate]
  );

  const idNumber = methods.watch('dataForm.idNumber');

  const isLoading = useMemo(() => !!userLoading || !!cpsLoading, [userLoading, cpsLoading]);

  const isSubmitDisabled = hasError || isLoading || !assignSite || !idNumber?.trim();

  const formSubmitHandler: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
    const site = sites?.find((site: Site) => site.name == assignSite);
    const dataCreateCp = {
      site_id: site?.id,
      cp_identification: data.dataForm.idNumber.toUpperCase().trim(),
      active: true
    };
    const country = site?.country || null;
    const created_by_first_name = userDetails?.firstName || null;
    const created_by_last_name = userDetails?.lastName || null;
    const response = await dispatch(
      createNewCp({
        cp: dataCreateCp,
        extra: { country, created_by_first_name, created_by_last_name, siteName: assignSite }
      })
    );
    const isSuccess = response?.meta?.requestStatus === 'fulfilled';
    if (isSuccess) {
      //we want to increase the cp count of the site in case it site cp
      if (site?.id) {
        dispatch(updateSiteCpCount({ sitesId: site.id, isNew: true }));
      }
      navigate(`/${pagesEnum.CP}`);
    }
  };

  const validateField = (event: any) => {
    const { id, value }: { id: keyof ICp; value: string } = event.target;

    if (value?.length) {
      if (id === 'idNumber' && value) {
        const exist = !!cps?.find((cp: ICp) => {
          return cp[id].toUpperCase() === value.toUpperCase();
        });
        if (exist) {
          setIdNumberDuplicate('Same CP id, Try other id');
          return;
        }
        setIdNumberDuplicate('');
      }
    }
  };

  const clearError = () => {
    dispatch(setError(undefined));
    setIdNumberDuplicate('');
  };

  if (isLoading) return <CircularIndeterminate />;
  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <FormProvider {...methods}>
        <form noValidate onChange={validateField} onSubmit={methods.handleSubmit(formSubmitHandler)}>
          <StyledCard sx={{ background: 'transparent' }}>
            <StyledCardContent>
              <Stack spacing={3}>
                <ConfirmDialog
                  openDialog={!!cpsError}
                  setOpenDialog={clearError}
                  onConfirm={clearError}
                  dialogTitle={'This CP is already assigned to another site'}
                  dialogContentText={'Press Okay to continue'}
                  confirmLabel='Okay'
                />
                <StyledTypographyTitle sx={{ justifyContent: 'flex-start' }}>Add New CP</StyledTypographyTitle>
                <Stack direction='column' spacing={2} flexWrap='wrap' useFlexGap>
                  <CustomInputForm
                    disabled={false}
                    errorMessage={errors?.dataForm?.idNumber?.message || idNumberDuplicate}
                    defaultValue={''}
                    controllerName={'dataForm.idNumber'}
                    inputRegister={'dataForm.idNumber'}
                    inputId={'idNumber'}
                    label={CPInputsFormLabels.ID_NUMBER}
                    type={'text'}
                    icon={<CpIcon />}
                  />
                  <SelectBox
                    key={'sites'}
                    withAllOption
                    allOptionLabel='Unrestricted'
                    items={sites.map((site) => site?.name ?? '').filter((name) => name.length)}
                    disabledItems={sites.filter((site) => !site.active).map((site) => site.name) as string[]}
                    values={assignSite}
                    label={'Assign to Site'}
                    inputLabel={'Assign to Site'}
                    onFilterChange={(_, filterValue) => {
                      setAssignSite(filterValue as string);
                    }}
                    inputId={'site'}
                    type={'sites'}
                  />
                </Stack>
              </Stack>
              <Stack direction='row' sx={{ display: 'flex', justifyContent: 'flex-end', m: 10 }}>
                <Button
                  color='secondary'
                  data-testid='cancel-button'
                  size='large'
                  variant='text'
                  onClick={() => {
                    navigate(`/${pagesEnum.CP}`);
                  }}
                >
                  cancel
                </Button>
                <Button
                  color='secondary'
                  variant='contained'
                  data-testid='save-button'
                  disabled={isSubmitDisabled}
                  type='submit'
                  size='large'
                >
                  save
                </Button>
              </Stack>
            </StyledCardContent>
          </StyledCard>
        </form>
      </FormProvider>
    </Box>
  );
};

export default NewCp;
